import React, { useEffect, useState } from 'react';
import ChannelChat from '../../components/ChannelChat';
import { normalizeContactId, normalizePhoneDigits } from '../../utils/messaging';
import { Card } from '../today/cards';
import { nextChangeOrderSteps, type ChangeOrderStatus } from '../changeOrders';
import { buildPaymentRoadmap, loanRequirementLabel, loanRoadmapNote, loanTriggerLabel, locateLoanTrigger, paymentRoadmapShortLabel, paymentRoadmapStripCells, percentLabel, type PaymentCondition } from '../paymentCondition';
import { UIcon, short } from './UnitCards';
import type { ChangeOrderRow, DocRow, unitAreas } from './unitExtras';
import type { UnitSummary } from './unitSummary';

const dateLabel = (ms: number | null | undefined) =>
  ms ? new Date(ms).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '';
const chip = (tone: string, text: React.ReactNode) => <span className={`ot-chip c-${tone}`}>{text}</span>;
const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;
export type LoadState = 'ready' | 'loading' | 'failed';
const loadNote = (state: LoadState, what: string) =>
  state === 'loading' ? `Loading ${what}…` : state === 'failed' ? `Couldn't load ${what}. Refresh the page to try again.` : '';

/* ── documents ── */
const DOC_FILTERS: Array<{ id: 'all' | DocRow['section']; label: string }> = [
  { id: 'all', label: 'All' },
  { id: 'main', label: 'Booking' },
  { id: 'loan', label: 'Loan' },
  { id: 'draft', label: 'Drafts' },
];

export function DocumentsCard({ docs, onManage, onRegenerate, onEditSanction, lockedTemplates = [], state = 'ready' }: {
  docs: DocRow[];
  onManage: () => void;
  /** Reopens the loan sanction form (details + letter) for an uploaded sanction letter. */
  onEditSanction?: (docKey: string) => void;
  /** Rebuilds a generated document through the booking drawer. */
  onRegenerate?: (templateId: string) => void;
  /** Templates that must not be regenerated (e.g. the booking form once the signed copy is in). */
  lockedTemplates?: string[];
  state?: LoadState;
}) {
  const [all, setAll] = useState(false);
  const [filter, setFilter] = useState<'all' | DocRow['section']>('all');
  const [editing, setEditing] = useState(false);
  // Regenerating: template id → the document's time when it started; done once the document is saved again.
  const [running, setRunning] = useState<Record<string, { since: number; at: number }>>({});
  const [slow, setSlow] = useState<Record<string, boolean>>({});
  useEffect(() => {
    setRunning((cur) => {
      const next = { ...cur };
      let changed = false;
      Object.entries(cur).forEach(([tid, r]) => {
        const row = docs.find((d) => d.templateId === tid);
        if (row && row.ms > r.since) {
          delete next[tid];
          changed = true;
        }
      });
      return changed ? next : cur;
    });
  }, [docs]);
  useEffect(() => {
    const ids = Object.keys(running);
    if (!ids.length) return;
    const t = window.setInterval(() => {
      const now = Date.now();
      setSlow((cur) => {
        const next = { ...cur };
        ids.forEach((id) => { next[id] = now - (running[id]?.at || now) > 45000; });
        return next;
      });
    }, 5000);
    return () => window.clearInterval(t);
  }, [running]);
  const regenerate = (d: DocRow) => {
    if (!onRegenerate || !d.templateId) return;
    setRunning((cur) => ({ ...cur, [d.templateId]: { since: d.ms, at: Date.now() } }));
    setSlow((cur) => ({ ...cur, [d.templateId]: false }));
    onRegenerate(d.templateId);
  };

  const count = (id: 'all' | DocRow['section']) => (id === 'all' ? docs.length : docs.filter((d) => d.section === id).length);
  const filtered = filter === 'all' ? docs : docs.filter((d) => d.section === filter);
  const shown = all ? filtered : filtered.slice(0, 6);
  const isSanction = (d: DocRow) => !d.templateId && (d.id.toLowerCase().replace(/[^a-z0-9]/g, '') === 'sanctionletter' || /sanction letter/i.test(d.label));
  const canRegenerateAny = (!!onRegenerate && docs.some((d) => d.templateId)) || (!!onEditSanction && docs.some(isSanction));
  return (
    <Card index={8} label="Documents">
      <div className="ot-hd">
        <div><h3>Documents</h3><p role={state === 'failed' ? 'alert' : undefined}>{loadNote(state, 'documents') || (docs.length ? `${plural(docs.length, 'document')} on this booking` : 'Nothing uploaded or generated yet')}</p></div>
        <div style={{ display: 'flex', gap: 6 }}>
          {canRegenerateAny ? (
            <button className="ot-btn sm" aria-pressed={editing} onClick={() => setEditing((v) => !v)} title={editing ? 'Done editing' : 'Edit documents (regenerate, sanction details)'}>
              {editing ? 'Done' : 'Edit'}
            </button>
          ) : null}
          <button className="ot-btn sm" onClick={onManage}>Upload or generate</button>
        </div>
      </div>
      {docs.length ? (
        <div className="ou-rchips" role="group" aria-label="Filter documents">
          {DOC_FILTERS.map((f) => {
            const n = count(f.id);
            if (f.id !== 'all' && !n && filter !== f.id) return null;
            return <button key={f.id} aria-pressed={filter === f.id} onClick={() => setFilter(f.id)}>{f.label}<b>{n}</b></button>;
          })}
        </div>
      ) : null}
      {filtered.length ? (
        <div className="ot-list">
          {shown.map((d) => {
            const busy = !!(d.templateId && running[d.templateId]);
            const locked = !!d.templateId && (d.replaced || lockedTemplates.includes(d.templateId));
            return (
              <div className={`ot-item${d.replaced ? ' ou-doc-replaced' : ''}`} key={`${d.id}-${d.templateId}`}>
                <span className="ot-ibox c-mute"><UIcon name="file" /></span>
                <span className="bd">
                  <span className="t" style={{ display: 'block' }}>
                    {d.label} {d.section === 'draft' ? chip('warn', 'Draft') : d.section === 'loan' ? chip('acc', 'Loan') : null}
                    {d.replaced ? chip('good', 'Signed copy uploaded') : null}
                  </span>
                  <span className="s" style={{ display: 'block' }}>
                    {busy ? (slow[d.templateId] ? 'Still regenerating… it can take up to a minute' : 'Regenerating…') : `${d.files.length ? plural(d.files.length, 'file') : 'No file attached'}${d.ms ? ` · ${dateLabel(d.ms)}` : ''}`}
                  </span>
                </span>
                {editing && d.templateId && onRegenerate ? (
                  <button
                    className="ot-btn sm ou-regen"
                    onClick={() => regenerate(d)}
                    disabled={busy || locked}
                    title={locked ? 'Locked: the signed copy is uploaded. Create a revised form to change it.' : 'Rebuild this document with the latest details'}
                    aria-busy={busy}
                  >
                    <svg className={`ot-ic${busy ? ' ou-spin' : ''}`} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M3 12a9 9 0 0 1 15.5-6.3L21 8" /><path d="M21 3v5h-5" /><path d="M21 12a9 9 0 0 1-15.5 6.3L3 16" /><path d="M3 21v-5h5" /></svg>
                    {busy ? 'Regenerating…' : locked ? 'Locked' : 'Regenerate'}
                  </button>
                ) : editing && onEditSanction && isSanction(d) ? (
                  <button className="ot-btn sm ou-regen" onClick={() => onEditSanction(d.id)} title="Change the sanction details or replace the letter">
                    <svg className="ot-ic" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M12 20h9" /><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" /></svg>
                    Edit details
                  </button>
                ) : d.files[0] ? (
                  <a className="ot-btn sm" href={d.files[0].url} target="_blank" rel="noopener noreferrer" aria-label={`Open ${d.label}`}>Open</a>
                ) : null}
              </div>
            );
          })}
        </div>
      ) : docs.length ? <p className="faint" style={{ margin: 0, fontSize: 13 }}>No documents in this group.</p> : null}
      {filtered.length > 6 ? (
        <button className="ot-btn sm" style={{ marginTop: 8 }} onClick={() => setAll((v) => !v)}>{all ? 'Show fewer' : `Show all ${filtered.length}`}</button>
      ) : null}
    </Card>
  );
}

/* ── change orders ── */
const CO_TONE: Record<string, string> = { requested: 'mute', reviewed: 'mute', approved: 'acc', assigned: 'acc', in_progress: 'acc', on_hold: 'warn', done: 'good', customer_confirmed: 'good', closed: 'good', rejected: 'bad' };
function ChangeOrderItem({ r, onStatus }: { r: ChangeOrderRow; onStatus?: (id: string, from: string, to: ChangeOrderStatus) => Promise<void> }) {
  const [more, setMore] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [busy, setBusy] = useState('');
  const [confirm, setConfirm] = useState<ChangeOrderStatus | null>(null);
  const [err, setErr] = useState('');
  const steps = nextChangeOrderSteps(r.status);
  const go = async (to: ChangeOrderStatus) => {
    if (!onStatus) return;
    if (to === 'rejected' && confirm !== 'rejected') return setConfirm('rejected');
    setBusy(to);
    setErr('');
    setConfirm(null);
    try {
      await onStatus(r.id, r.status, to);
      setMore(false);
    } catch (e: any) {
      setErr(e?.message || "Couldn't change the status.");
    } finally {
      setBusy('');
    }
  };
  const long = r.description.length > 140;
  return (
    <div className="ou-co">
      <div className="ou-co-hd">
        <span className="t"><span className="mono faint">{r.coNo}</span> {r.title}</span>
        {chip(r.waitingBuyer && r.waitingDays > 7 ? 'warn' : CO_TONE[r.status] || 'mute', r.statusLabel)}
      </div>
      {r.description ? (
        <p className={`ou-co-desc${expanded ? ' open' : ''}`}>
          {r.description}
        </p>
      ) : null}
      {long ? <button className="ou-linkbtn" onClick={() => setExpanded((v) => !v)}>{expanded ? 'Less' : 'More'}</button> : null}
      <span className="ou-co-meta">
        {[r.agency, r.waitingBuyer ? `waiting on buyer ${r.waitingDays} day${r.waitingDays === 1 ? '' : 's'}` : '', r.costImpact ? 'cost impact' : '', dateLabel(r.ms)].filter(Boolean).join(' · ')}
      </span>
      {onStatus && (steps.main || steps.more.length) ? (
        <div className="ou-co-act">
          {steps.main ? (
            <button className="ot-btn sm dark" onClick={() => go(steps.main!.to)} disabled={!!busy}>{busy === steps.main.to ? 'Saving…' : steps.main.label}</button>
          ) : null}
          {steps.more.length ? (
            more ? (
              steps.more.map((m) => (
                <button key={m.to} className={`ot-btn sm${m.to === 'rejected' ? ' ou-co-danger' : ''}`} onClick={() => go(m.to)} disabled={!!busy}>
                  {busy === m.to ? 'Saving…' : confirm === m.to ? `Yes, ${m.label.toLowerCase()}` : m.label}
                </button>
              ))
            ) : (
              <button className="ou-linkbtn" onClick={() => setMore(true)} aria-label={`More actions for ${r.coNo}`}>More actions</button>
            )
          ) : null}
          {confirm === 'rejected' ? <span className="faint" style={{ fontSize: 12 }}>Tap again to reject.</span> : null}
        </div>
      ) : null}
      {err ? <p className="ou-co-err" role="alert">{err}</p> : null}
    </div>
  );
}

export function ChangeOrdersCard({ rows, onOpen, onStatus, state = 'ready' }: { rows: ChangeOrderRow[]; onOpen: () => void; onStatus?: (id: string, from: string, to: ChangeOrderStatus) => Promise<void>; state?: LoadState }) {
  const open = rows.filter((r) => r.open).length;
  const [all, setAll] = useState(false);
  const shown = all ? rows : rows.slice(0, 5);
  return (
    <Card index={9} label="Change orders">
      <div className="ot-hd">
        <div><h3>Change orders</h3><p role={state === 'failed' ? 'alert' : undefined}>{loadNote(state, 'change orders') || (rows.length ? `${open} open of ${rows.length}` : 'No changes requested by the buyer')}</p></div>
        <button className="ot-btn sm" onClick={onOpen}>{rows.length ? 'Manage' : 'New request'}</button>
      </div>
      {rows.length ? <div className="ou-co-list">{shown.map((r) => <ChangeOrderItem key={r.id} r={r} onStatus={onStatus} />)}</div> : null}
      {rows.length > 5 ? <button className="ot-btn sm" style={{ marginTop: 8 }} onClick={() => setAll((v) => !v)}>{all ? 'Show fewer' : `Show all ${rows.length}`}</button> : null}
    </Card>
  );
}

/* ── areas ── */
export function AreasCard({ areas }: { areas: ReturnType<typeof unitAreas> }) {
  return (
    <Card index={10} label="Areas">
      <div className="ot-hd"><div><h3>Areas</h3></div></div>
      {areas.rows.map((r) => (
        <div className="ot-kv" key={r.label}><span className="muted">{r.label}</span><span className="num">{r.sqmt} sq mt</span></div>
      ))}
      {areas.totalSqmt > 0 ? (
        <div className="ot-statrow" style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid var(--ot-line)' }}>
          <div><div className="ot-lbl">Total</div><div className="ot-mid num">{areas.totalSqft.toLocaleString('en-IN')} sq ft</div><div className="faint num" style={{ fontSize: 12 }}>{Math.round(areas.totalSqmt * 100) / 100} sq mt</div></div>
          {areas.sbSqft ? <div><div className="ot-lbl">Super built-up</div><div className="ot-mid num">{areas.sbSqft.toLocaleString('en-IN')} sq ft</div><div className="faint" style={{ fontSize: 12 }}>{areas.sbPercent}% loading</div></div> : null}
        </div>
      ) : null}
    </Card>
  );
}

/* ── collection snapshot ── */
export function CollectionCard({ s }: { s: UnitSummary }) {
  const c = s.collection;
  const pct = (v: number, of: number) => `${of > 0 ? ((Math.max(0, v) / of) * 100).toFixed(1) : '0.0'}%`;
  const inr = (v: number) => `₹${Math.round(v).toLocaleString('en-IN')}`;
  return (
    <Card className="ou-wide" index={4} label="Collection snapshot">
      <div className="ot-hd"><div><h3>Collection snapshot</h3></div></div>
      <div className="ot-tbl-wrap">
        <table className="ou-coll">
          <thead><tr><th>Collection</th><th className="r">Amount</th><th className="r">Collected</th><th className="r">Pending</th></tr></thead>
          <tbody>
            {[c.total, ...c.parts].map((r, i) => (
              <tr key={r.label} className={i === 0 ? 'tot' : ''}>
                <td>{r.label}</td>
                <td className="r num">{inr(r.amount)}</td>
                <td className="r num good">{inr(r.received)}{i > 0 ? <span className="sub">{pct(r.received, r.amount)} collected</span> : null}</td>
                <td className="r num warn">{inr(r.pending)}{i > 0 ? <span className="sub">{pct(r.pending, r.amount)} balance</span> : null}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

/* ── payment milestones and loan plan ── */
export function MilestonesCard({ condition, bookingDate, legacyTerms, unitValue, onEdit }: { condition: PaymentCondition | null | undefined; bookingDate: string; legacyTerms?: string | null; unitValue: number; onEdit?: () => void }) {
  const [open, setOpen] = useState(false);
  const items = condition ? buildPaymentRoadmap(condition, bookingDate) : [];
  const cells = paymentRoadmapStripCells(items);
  const dpPercent = items[0]?.percent || 0;
  const loan = condition?.loan;
  const loanAt = condition ? locateLoanTrigger(condition, items) : null;
  const amt = (pct: number) => (unitValue > 0 ? short((unitValue * pct) / 100) : '');
  const loanBox = loan ? (
    <div className="ou-loanplan">
      <span className="ot-ibox c-acc"><UIcon name="bank" /></span>
      <span className="bd">
        <b>Loan · {loanRequirementLabel(loan)}</b>
        <span className="s">{loanTriggerLabel(loan.trigger)}</span>
        <span className="s faint">{loanRoadmapNote}</span>
      </span>
    </div>
  ) : null;
  return (
    <Card index={5} label="Payment milestones and loan plan">
      <div className="ot-hd">
        <div><h3>Payment milestones</h3><p>{items.length ? `DP ${percentLabel(dpPercent)} · Remaining ${percentLabel(100 - dpPercent)}` : 'No structured payment plan on this booking'}</p></div>
        {onEdit ? <button className="ot-btn sm" onClick={onEdit}>{items.length ? 'Edit' : 'Add plan'}</button> : null}
      </div>
      {!items.length ? (
        <p className="faint" style={{ margin: 0, fontSize: 13 }}>{legacyTerms ? `Written terms: ${legacyTerms}. Add the plan in the booking form to see milestones and due dates.` : 'Add the plan in the booking form to see milestones and due dates.'}</p>
      ) : (
        <>
          <div className="ou-strip" style={{ ['--n' as any]: cells.length }}>
            {cells.map((c, i) => (
              <div key={`${c.stageIndex}-${i}`} className={`ou-cell${c.stageIndex === 0 ? ' dp' : ''}${i > 0 && c.stageIndex !== 0 && cells[i - 1].stageIndex === 0 ? ' split' : ''}`} title={c.due}>
                <span className="m">{paymentRoadmapShortLabel(c)}</span>
                <b className="num">{percentLabel(c.percent)}</b>
                {amt(c.percent) ? <span className="a num">{amt(c.percent)}</span> : null}
              </div>
            ))}
          </div>
          {loanBox}
          <button className="ou-more" onClick={() => setOpen((v) => !v)} aria-expanded={open}>
            {open ? 'Hide' : 'Show'} milestone details and due dates
            <svg className="ot-ic" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ transform: open ? 'rotate(180deg)' : undefined }}><path d="m6 9 6 6 6-6" /></svg>
          </button>
          {open ? (
            <div className="ou-msl">
              {items.map((it, si) => (
                <div key={it.id} className="ou-ms">
                  <div className="row head"><span><b>{it.label}</b><span className="faint"> · {it.due}</span></span><span className="num"><b>{percentLabel(it.percent)}</b>{amt(it.percent) ? <span className="faint"> · {amt(it.percent)}</span> : null}</span></div>
                  {it.instalments?.map((r, ri) => (
                    <React.Fragment key={`${r.label}-${r.due}`}>
                      <div className="row"><span>{r.label}<span className="faint"> · {r.due}</span></span><span className="num">{percentLabel(r.percent)}{amt(r.percent) ? <span className="faint"> · {amt(r.percent)}</span> : null}</span></div>
                      {loanAt?.stage === si && loanAt.row === ri ? <div className="row loan">Loan request triggers here</div> : null}
                    </React.Fragment>
                  ))}
                  {loanAt?.stage === si && loanAt.row === -1 ? <div className="row loan">Loan request triggers here</div> : null}
                </div>
              ))}
            </div>
          ) : null}
        </>
      )}
    </Card>
  );
}

/* ── key dates ── */
export function DatesCard({ s }: { s: UnitSummary }) {
  const rows: Array<[string, number | null, string?]> = [
    ['Booking form', s.dates.form?.getTime() || null],
    ['Booked (first receipt)', s.dates.booking?.getTime() || null, 'No receipt yet'],
    ['Agreement registered', s.dates.agreement?.getTime() || null, s.received > 0 ? 'Not yet' : undefined],
    ['Loan sanctioned', s.loan.needed ? s.sanctionMs : null, s.loan.needed ? 'Not yet' : undefined],
    ['Last receipt', s.lastReceipt?.ms || null, 'None yet'],
    ['Next due', s.nextDue?.dueMs || null, s.hasPlan ? undefined : 'No plan'],
    ['Sale deed', s.dates.deed?.getTime() || null, 'Not yet'],
  ];
  return (
    <Card index={11} label="Key dates">
      <div className="ot-hd"><div><h3>Key dates</h3><p>{s.daysSinceBooking ? `${plural(s.daysSinceBooking, 'day')} since booking` : 'Booking dates'}</p></div></div>
      {rows
        .filter(([, ms, fallback]) => ms || fallback)
        .map(([label, ms, fallback]) => (
          <div className="ot-kv" key={label}>
            <span className="muted">{label}</span>
            {ms ? <span className="num">{dateLabel(ms)}{label === 'Next due' && s.nextDue ? <span className="faint"> · {short(s.nextDue.amount - s.nextDue.paid)}</span> : null}</span> : <span className="faint">{fallback}</span>}
          </div>
        ))}
    </Card>
  );
}

/* ── WhatsApp ── */
const WA_OPEN_KEY = 'unit-page:whatsapp-open';
export function WhatsAppCard(props: { s: UnitSummary; unitId: string; unitNumber: string; projectId: string; openSignal?: number }) {
  const buyer = props.s.buyers.find((b) => b.primary) || props.s.buyers[0];
  const digits = normalizePhoneDigits(String(buyer?.mobile || ''));
  // Remembers whether the chat was left open (per browser), so it stays open on every unit until hidden.
  const [open, setOpenState] = useState(() => {
    try { return window.localStorage.getItem(WA_OPEN_KEY) === '1'; } catch { return false; }
  });
  const setOpen = (next: boolean | ((v: boolean) => boolean)) =>
    setOpenState((v) => {
      const value = typeof next === 'function' ? next(v) : next;
      try { window.localStorage.setItem(WA_OPEN_KEY, value ? '1' : '0'); } catch { /* ignore */ }
      return value;
    });
  // The phone bottom bar asks to open the chat: expand it and bring it into view.
  useEffect(() => {
    if (!props.openSignal) return;
    setOpenState(true);
    window.setTimeout(() => document.querySelector('[aria-label="WhatsApp"]')?.scrollIntoView({ block: 'start', behavior: 'smooth' }), 60);
  }, [props.openSignal]);
  return (
    <Card index={12} label="WhatsApp">
      <div className="ot-hd">
        <div><h3>WhatsApp</h3><p>{buyer ? `${buyer.name}${digits ? '' : ' · no mobile saved'}` : 'No buyer on this booking'}</p></div>
        {digits ? <button className="ot-btn sm" onClick={() => setOpen((v) => !v)} aria-expanded={open}>{open ? 'Hide chat' : 'Show chat'}</button> : null}
      </div>
      {digits && open ? (
        <div style={{ border: '1px solid var(--ot-line)', borderRadius: 14, overflow: 'hidden', background: '#fff' }}>
          <ChannelChat
            channel="whatsapp"
            contactId={normalizeContactId('whatsapp', digits)}
            contactName={buyer?.name || null}
            customerUnitId={props.unitId}
            customerUnitNumber={props.unitNumber}
            customerProjectName=""
            customerName={buyer?.name || ''}
            projectId={props.projectId}
            height={380}
            showHeader={false}
            canOpenCustomerUnit={false}
          />
        </div>
      ) : null}
    </Card>
  );
}
